import { apiClient } from '@/lib/api';
import { ENDPOINTS } from '@/lib/api/endpoints';
import {
  Material,
  CreateMaterialData,
  UpdateMaterialData,
  MaterialTariffHistory,
  CreateTariffData,
  ServiceActivity,
  CreateServiceActivityData,
  PriceList,
  CreatePriceListData,
  PriceListEntryData,
} from '../types/material.types';

class MaterialService {
  async getMaterials(): Promise<Material[]> {
    return apiClient.get<Material[]>(ENDPOINTS.MATERIALS);
  }

  async getMaterial(id: string): Promise<Material> {
    return apiClient.get<Material>(ENDPOINTS.MATERIAL_BY_ID(id));
  }

  async createMaterial(data: CreateMaterialData): Promise<Material> {
    return apiClient.post<Material>(ENDPOINTS.MATERIALS, data);
  }

  async updateMaterial(id: string, data: UpdateMaterialData): Promise<Material> {
    return apiClient.put<Material>(ENDPOINTS.MATERIAL_BY_ID(id), data);
  }

  async deleteMaterial(id: string): Promise<void> {
    return apiClient.delete<void>(ENDPOINTS.MATERIAL_BY_ID(id));
  }

  async addTariff(materialId: string, data: CreateTariffData): Promise<Material> {
    return apiClient.post<Material>(ENDPOINTS.MATERIAL_TARIFF(materialId), data);
  }

  async getTariffHistory(materialId: string): Promise<MaterialTariffHistory[]> {
    return apiClient.get<MaterialTariffHistory[]>(ENDPOINTS.MATERIAL_TARIFF_HISTORY(materialId));
  }

  // Service Activities
  async getActivities(materialId: string): Promise<ServiceActivity[]> {
    return apiClient.get<ServiceActivity[]>(ENDPOINTS.MATERIAL_ACTIVITIES(materialId));
  }

  async createActivity(
    materialId: string,
    data: CreateServiceActivityData
  ): Promise<ServiceActivity> {
    return apiClient.post<ServiceActivity>(ENDPOINTS.MATERIAL_ACTIVITIES(materialId), data);
  }

  async updateActivity(
    materialId: string,
    activityId: string,
    data: CreateServiceActivityData
  ): Promise<ServiceActivity> {
    return apiClient.put<ServiceActivity>(
      ENDPOINTS.MATERIAL_ACTIVITY_BY_ID(materialId, activityId),
      data
    );
  }

  async deleteActivity(materialId: string, activityId: string): Promise<void> {
    return apiClient.delete<void>(ENDPOINTS.MATERIAL_ACTIVITY_BY_ID(materialId, activityId));
  }

  // Image Upload
  async uploadImage(materialId: string, file: File): Promise<{ imageUrl: string }> {
    const formData = new FormData();
    formData.append('image', file);
    // Pass FormData directly; do NOT set Content-Type manually so browser sets multipart boundary
    return apiClient.post<{ imageUrl: string }>(ENDPOINTS.MATERIAL_IMAGE(materialId), formData);
  }

  async deleteImage(materialId: string): Promise<void> {
    return apiClient.delete<void>(ENDPOINTS.MATERIAL_IMAGE(materialId));
  }

  // Price Lists
  async getPriceLists(): Promise<PriceList[]> {
    return apiClient.get<PriceList[]>(ENDPOINTS.PRICE_LISTS);
  }

  async getPriceList(id: string): Promise<PriceList> {
    return apiClient.get<PriceList>(ENDPOINTS.PRICE_LIST_BY_ID(id));
  }

  async createPriceList(data: CreatePriceListData): Promise<PriceList> {
    return apiClient.post<PriceList>(ENDPOINTS.PRICE_LISTS, data);
  }

  async updatePriceList(id: string, data: CreatePriceListData): Promise<PriceList> {
    return apiClient.put<PriceList>(ENDPOINTS.PRICE_LIST_BY_ID(id), data);
  }

  async deletePriceList(id: string): Promise<void> {
    return apiClient.delete<void>(ENDPOINTS.PRICE_LIST_BY_ID(id));
  }

  async upsertPriceListEntries(
    priceListId: string,
    entries: PriceListEntryData[]
  ): Promise<PriceList> {
    return apiClient.put<PriceList>(ENDPOINTS.PRICE_LIST_ENTRIES(priceListId), { entries });
  }

  async deletePriceListEntry(priceListId: string, entryId: string): Promise<void> {
    return apiClient.delete<void>(ENDPOINTS.PRICE_LIST_ENTRY_BY_ID(priceListId, entryId));
  }
}

export const materialService = new MaterialService();
export default materialService;
